/**
 * Pure helpers for BLI permissions editor (tri-state tree, diff, dependencies).
 */

/** RS4 sintético: módulo en ServRoleModulesBli sin acciones asignadas en ServRoleModuleActionsBli (puede persistirse en PUT). */
export const BLI_ROLE_MODULE_ONLY_CODE = '__bli_role_module__';

/**
 * Entrada solo para el diff / contador “cambios pendientes”: al desmarcar el módulo en el árbol se retiran
 * acciones + asociación; esto cuenta la parte “módulo” además de cada acción (~1 fila lógica en UI).
 */
export const BLI_DIFF_MODULE_ACCESS_CLEAR = '__bli_diff_module_access_clear__';

export function deepClonePerms(perms) {
  return JSON.parse(JSON.stringify(perms));
}

/** Tras renombrar el código de una acción en catálogo, migrar flags en memoria por rol. */
export function migrateActionCodeInPerms(perms, moduleId, oldCode, newCode) {
  const mid = String(moduleId);
  const o = String(oldCode);
  const n = String(newCode);
  if (!perms || o === n) return perms;
  const next = deepClonePerms(perms);
  Object.keys(next).forEach((rid) => {
    const slice = next[rid]?.[mid];
    if (!slice || !Object.prototype.hasOwnProperty.call(slice, o)) return;
    const v = slice[o];
    delete slice[o];
    slice[n] = v;
  });
  return next;
}

export function getActionsForModule(moduleId, standardActions, customActionsByModuleId) {
  const mid = String(moduleId);
  const std = standardActions || [];
  const custom = (customActionsByModuleId && customActionsByModuleId[mid]) || [];
  return [...std, ...custom];
}

export function modChildren(modules, parentId) {
  const pid = parentId == null ? null : String(parentId);
  return modules.filter((m) => {
    const p = m.parentModuleId ?? m.parent ?? null;
    const pStr = p == null ? null : String(p);
    return pStr === pid;
  });
}

export function collectDescendantModuleIds(moduleId, modules) {
  const id = String(moduleId);
  const out = new Set([id]);
  const walk = (parent) => {
    modChildren(modules, parent).forEach((c) => {
      const cid = String(c.id ?? c.moduleId);
      if (!out.has(cid)) {
        out.add(cid);
        walk(cid);
      }
    });
  };
  walk(id);
  return [...out];
}

/** How many actions are on / total for module subtree */
export function moduleSubtreeProgress(roleId, rootModuleId, perms, modules, standardActions, customByModule) {
  const rid = String(roleId);
  const ids = collectDescendantModuleIds(rootModuleId, modules);
  let total = 0;
  let active = 0;
  ids.forEach((mid) => {
    const acts = getActionsForModule(mid, standardActions, customByModule);
    if (acts.length > 0) {
      /**
       * `__bli_role_module__` (RS4 sintético) significa rol+módulo en ServRoleModulesBli sin filas
       * en ServRoleModuleActionsBli — no debe tratarse como “todas las acciones del catálogo ON”.
       * Solo contamos flags por código; si solo viene el sintético, active queda 0 aunque haya 1 acción.
       */
      acts.forEach((a) => {
        total += 1;
        if (perms[rid]?.[mid]?.[a.code]) active += 1;
      });
    } else if (perms[rid]?.[mid]?.[BLI_ROLE_MODULE_ONLY_CODE]) {
      total += 1;
      active += 1;
    }
  });
  return { total, active };
}

/** Toggle derecho: `__bli_role_module__` no enciende cada acción si hay catálogo; el árbol sí marca el módulo “activo”. */
export function isActionEnabledInSlice(slice, actionCode, catalogActionCount) {
  if (!slice) return false;
  if (slice[actionCode]) return true;
  return catalogActionCount === 0 && !!slice[BLI_ROLE_MODULE_ONLY_CODE];
}

/**
 * Tri-state del árbol: `__bli_role_module__` = módulo asociado al rol (RS4 sintético).
 * Si está activo, el checkbox queda marcado aunque ninguna acción del catálogo esté ON (0/N).
 * Para quitar asociación y apagar acciones, el usuario desmarca el módulo en el árbol (`toggleModuleAll`).
 */
export function moduleTriState(roleId, moduleId, perms, modules, standardActions, customByModule) {
  const rid = String(roleId);
  const mid = String(moduleId);
  if (perms[rid]?.[mid]?.[BLI_ROLE_MODULE_ONLY_CODE]) {
    return 'checked';
  }
  const { total, active } = moduleSubtreeProgress(
    roleId,
    moduleId,
    perms,
    modules,
    standardActions,
    customByModule
  );
  if (total === 0) return 'unchecked';
  if (active === 0) return 'unchecked';
  if (active === total) return 'checked';
  return 'indeterminate';
}

export function computeDiff(perms, saved, roles, modules, standardActions, customByModule, selectedRoleId) {
  if (selectedRoleId == null) {
    return { added: 0, removed: 0, changes: [] };
  }
  const rid = String(selectedRoleId);
  let added = 0;
  let removed = 0;
  const changes = [];
  modules.forEach((mod) => {
    const mid = String(mod.id ?? mod.moduleId);
    const modLabel = mod.label ?? mod.moduleName ?? mid;
    const acts = getActionsForModule(mid, standardActions, customByModule);
    acts.forEach((a) => {
      const cur = !!(perms[rid]?.[mid]?.[a.code]);
      const was = !!(saved[rid]?.[mid]?.[a.code]);
      if (cur !== was) {
        if (cur) added += 1;
        else removed += 1;
        changes.push({
          mod: modLabel,
          moduleId: mid,
          code: a.code,
          label: a.label,
          was,
          cur,
        });
      }
    });
    const curMo = !!(perms[rid]?.[mid]?.[BLI_ROLE_MODULE_ONLY_CODE]);
    const wasMo = !!(saved[rid]?.[mid]?.[BLI_ROLE_MODULE_ONLY_CODE]);
    if (acts.length === 0) {
      if (curMo !== wasMo) {
        if (curMo) added += 1;
        else removed += 1;
        changes.push({
          mod: modLabel,
          moduleId: mid,
          code: BLI_ROLE_MODULE_ONLY_CODE,
          label: modLabel,
          was: wasMo,
          cur: curMo,
        });
      }
    } else if (curMo !== wasMo) {
      if (curMo) added += 1;
      else removed += 1;
      changes.push({
        mod: modLabel,
        moduleId: mid,
        code: BLI_ROLE_MODULE_ONLY_CODE,
        label: modLabel,
        was: wasMo,
        cur: curMo,
      });
    }

    if (acts.length > 0) {
      const triBefore = moduleTriState(rid, mid, saved, modules, standardActions, customByModule);
      const triAfter = moduleTriState(rid, mid, perms, modules, standardActions, customByModule);
      const bliRemovalAlreadyCounted = wasMo && !curMo;
      if (triBefore !== 'unchecked' && triAfter === 'unchecked' && !bliRemovalAlreadyCounted) {
        removed += 1;
        changes.push({
          mod: modLabel,
          moduleId: mid,
          code: BLI_DIFF_MODULE_ACCESS_CLEAR,
          labelKey: 'dashboard.configuration.diffModuleAccessRemoved',
          was: true,
          cur: false,
        });
      }
    }
  });
  return { added, removed, changes };
}

export function countDiffForRole(perms, saved, roles, modules, standardActions, customByModule, roleId) {
  const { added, removed } = computeDiff(perms, saved, roles, modules, standardActions, customByModule, roleId);
  return added + removed;
}

/** Ensure dependency codes exist when enabling an action */
export function withDepsEnabled(permsSlice, action, enabled) {
  const next = { ...permsSlice };
  if (!enabled) {
    next[action.code] = false;
    return next;
  }
  next[action.code] = true;
  (action.deps || []).forEach((dep) => {
    if (!next[dep]) next[dep] = true;
  });
  return next;
}

/** When revoking 'view', revoke actions that depend on it */
export function afterRevokeView(permsSlice, standardActions, customActions) {
  const all = [...(standardActions || []), ...(customActions || [])];
  const next = { ...permsSlice };
  if (!next.view) {
    all.forEach((a) => {
      if ((a.deps || []).includes('view')) {
        next[a.code] = false;
      }
    });
  }
  return next;
}
